import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { roles } from '../../app/shared/lists/roles';
import { MKG_SPEED_DIAL_STATE, MkgSpeedDialButtonChild, MkgSpeedDialConfig } from '../shared/components/speed-dial/speed-dial.component';
import { DataService } from './data.service';
import { UserService } from './user.service';
import { MEKANIC_MENU } from '../component/other/header/menu/mekanic-menu';
import { companyStates } from '../shared/lists/company-states';
import { TCAR_INTEGRATED_MENU } from '../component/other/header/menu/tcar-integrated-menu';
import { isDifferentRoutePath } from './layout-navigation.util';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  menu: {
    sideNavExpanded: boolean;
    expandAll: boolean;
    newUsers: number; 
    elementExpandend?: {
      component: string;
      child: string;
    }
  } = {
    sideNavExpanded: true,
    expandAll: false,
    elementExpandend: {
      component: undefined,
      child: undefined
    },
    newUsers: 0
  };

  message: string;

  invoiceButtons : MkgSpeedDialButtonChild[] = [{
    label: "NFe",
    matColor: "primary",
    onClick: () => this.newInvoice('NFe')
  },
  {
    label: "NFCe",
    matColor: "primary",
    onClick: () => this.newInvoice('NFCe')
  },
  {
    label: "NFSe",
    matColor: "primary",
    onClick: () => this.newInvoice('NFSe')
  },
  {
    label: "OIP",
    matColor: "primary",
    onClick: () => this.newInvoice('OIP')
  },
  {
    label: "OIS",
    matColor: "primary",
    onClick: () => this.newInvoice('OIS')
  }];

  add: () => void;
  join: () => void;
  goBack: () => void;
  rollUp: () => void;
  newInvoice: (invoiceType) => void;
  fabs: {
    key: string;
    config: MkgSpeedDialConfig;
    initialState: MKG_SPEED_DIAL_STATE;
  }[] = [];


  invoiceSpeedDialConfig: MkgSpeedDialConfig = {
    direction: 'top',
    triggerButton: {
      icon: "add",
      matColor: "accent"
    },
    buttons: []
  }

  newUserName: string;
  countdown: number = 0;
  timeout: number = 60 * 1000; // request every 60 seconds
  static counting = false;
  /** Atribuido quando for mobile*/
  isHandset$: BehaviorSubject<boolean>;

  private _loader = false;
  private _component: string;
  private _loaderTriggers: Array<string> = []

  get loaderTriggerLength() {
    return this._loaderTriggers.length;
  }

  constructor(
    private dataService: DataService,
    private userService: UserService,
    private breakpointObserver: BreakpointObserver,
    private router: Router
  ) {

    const isHandsetBreakpoint$ = this.breakpointObserver
      .observe([Breakpoints.Handset, Breakpoints.Small])
      .pipe(
        map(result => result.matches),
        shareReplay(1)
      );

    const isTabletBreakpoint$ = this.breakpointObserver
      .observe([Breakpoints.Tablet])
      .pipe(
        map(result => result.matches),
        shareReplay(1)
      );

    const isLandscape$ = new BehaviorSubject<boolean>(window.innerWidth > window.innerHeight);

    window.addEventListener('resize', () => {
      isLandscape$.next(window.innerWidth > window.innerHeight);
    });

    this.isHandset$ = combineLatest([isHandsetBreakpoint$, isTabletBreakpoint$, isLandscape$]).pipe(
      map(([isHandset, isTablet, isLandscape]) => {
        if (isHandset) {
          return true;
        }
        if (isTablet && !isLandscape) {
          return true;
        }
        return false;
      }),
      shareReplay(1)
    ) as BehaviorSubject<boolean>;

    if (typeof localStorage !== 'undefined') {
      // One-time legacy-key purge: 'pageSize' is no longer written (see STORAGE_KEYS, marked deprecated).
      // Kept to clear stale values still present in older browsers.
      localStorage.removeItem('pageSize')
    }

    if (typeof window !== 'undefined') {
      window.ononline = () => {
        this.message = undefined;
        this.loader = false
      };
      window.onoffline = () => {
        this.message = "Sem acesso à Internet";
        this.loader = true
      };
    }

    combineLatest([
      this.dataService.company$,
      this.dataService.user$
    ]).subscribe(([company, user]) => {
      if (company && user) {
        this.getConfig();
      }
    })

    let previousUrl = this.router.url;

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(event => {
      const changedScreen = isDifferentRoutePath(previousUrl, event.url);
      previousUrl = event.url;

      if (!changedScreen || this.isHandset$.value) {
        return;
      }

      if (this.dataService.user?.sidenavCollapsedByDefault) {
        this.collapseAll(true);
      }
    });
  }


  /** Get sidenav config from localStorage */
  private getConfig() {
    if (typeof localStorage !== 'undefined') {
      const storageConfig = localStorage.getItem('sidenav');

      if (storageConfig) {
        const config = JSON.parse(storageConfig);

        this.menu = config;
      }
    }
  }

  private setConfig() {
    localStorage.setItem('sidenav', JSON.stringify(this.menu));
  }

  get loader(): boolean {
    return this._loader;
  }

  /** Atention, avoid to set loader if you are inputing something, the window will lose focus */
  set loader(loader: boolean) {
    const activeElement = window.document.activeElement as HTMLElement;

    if (loader) {
      activeElement.blur();
    };

    setTimeout(() => {
      this._loader = loader;
    });
  }

  addLoaderTrigger(functionName: string) {
    if (this._loaderTriggers.includes(functionName)) {
      console.warn(`A função ${functionName} já ativou o loader`)
    } 
    
    this._loaderTriggers.push(functionName);
    this.loader = Boolean(this._loaderTriggers.length);
  }

  removeLoaderTrigger(functionName: string) {
    if (!this._loaderTriggers.includes(functionName)) {
      console.warn(`A função ${functionName} não ativou o loader`);
    }
    const functionIndex = this._loaderTriggers.findIndex(trigger => trigger === functionName);
     
    if (functionIndex !== -1) {
      this._loaderTriggers.splice(functionIndex, 1);
    }
    this.loader = Boolean(this._loaderTriggers.length);
  }

  clearLoaderTriggerQueue() {
    this._loaderTriggers = [];
    this.loader = Boolean(this._loaderTriggers.length);
  }
  
  get component(): string {
    return this._component;
  }

  set component(component: string) {
    setTimeout(() => this._component = component);
    LayoutService.counting = true;
  }

  async lookForNewUsers() {
    LayoutService.counting = true;
    if (!this.dataService.company) {
      return
    }
    if (this.dataService.company.cnpj && this.dataService.userRole == roles.admin.id) {
      try {
        const users = await this.userService.getRequests();
        this.menu["newUsers"] = users.length
        if (!!users.length) {
          this.newUserName = users[users.length - 1].name
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  toggleSidenav() {
    this.menu["sideNavExpanded"] = !this.menu["sideNavExpanded"];

    if (!this.menu["sideNavExpanded"]) {
      this.menu.elementExpandend = { component: undefined, child: undefined };

      this.menu["expandAll"] = false;
    }

    this.setConfig();
  }

  toggle(component: string, isTopLevel: boolean) {
    this.menu["expandAll"] = false;
    this.menu["sideNavExpanded"] = true;

    if (isTopLevel) {
      this.menu["elementExpandend"] = {
        component: component === this.menu.elementExpandend?.component ? undefined : component,
        child: undefined
      }
    } else {
      this.menu["elementExpandend"]["child"] = component === this.menu.elementExpandend?.child ? undefined : component;
    }

    this.setConfig();
  }

  collapseAll(closeSideNav: boolean) {
    const newUsers = this.menu.newUsers;
    
    this.menu = {
      expandAll: false,
      sideNavExpanded: !closeSideNav,
      elementExpandend: {
        child: undefined,
        component: undefined
      },
      newUsers
    }
  }
  
  reset(closeMenu: boolean) {
    const newUsers = this.menu.newUsers;

    this.component = '';
    this.add = null;
    this.join = null;
    this.goBack = null;
    this.rollUp = null;
    this.newInvoice = null;

    if (closeMenu) {
      this.menu = {
        sideNavExpanded: false,
        expandAll: false,
        elementExpandend: {
          child: undefined,
          component: undefined
        },
        newUsers
      }
    }
  }

  scrollToElement($element: HTMLElement, horizontal: boolean): void {
    if ($element) {
      const inline = horizontal ? 'center' : 'nearest';
      // let blockOption: ScrollLogicalPosition;
      // switch ($element.id) {
      //   case this.toc[0].id:
      //     blockOption = <ScrollLogicalPosition>"center";
      //     break;
      //   case this.toc[this.toc.length - 1].id:
      //     blockOption = <ScrollLogicalPosition>"end";
      //     break;
      //   default: blockOption = <ScrollLogicalPosition>"start"
      //     break;
      // }
      $element.scrollIntoView({ behavior: "smooth", block: "center", inline: inline });
    }
  }

  public support() {
    const button = document.getElementById('tecincocrm-widget-button')
    if(button){
      button.click();
      return;
    }
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      window.open('https://api.whatsapp.com/send?phone=554599800231&amp;text=Olá, estou entrando em contato pelo site', '_blank')
    } else {
      window.open('https://web.whatsapp.com/send?phone=554599800231&amp;text=Olá, estou entrando em contato pelo site', '_blank')
    }
  }

  removeFab(key: string) {
    const fabIndex = this.fabs.findIndex(fab => fab.key === key);
    if (fabIndex >= 0) {
      this.fabs.splice(fabIndex, 1);
    }
  }

}
